import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Register() {
  const { t, lang } = useI18n();
  const { register } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isAr = lang === "ar";
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error(
        isAr
          ? "يجب الموافقة على الشروط والأحكام وسياسة الخصوصية قبل إنشاء الحساب."
          : "You must accept the Terms & Privacy Policy before creating an account."
      );
      return;
    }
    setBusy(true);
    try {
      const data = await register(form.name, form.email, form.password, form.phone || undefined);
      // Pass through ``next`` so the user lands back on the AI page
      // they originally tried to reach after verification.
      const params = new URLSearchParams(location.search);
      const nextParam = params.get("next");
      const nextSuffix = nextParam ? `&next=${encodeURIComponent(nextParam)}` : "";

      // Fallback path while Resend DNS records are still propagating:
      // the backend exposes the OTP code in the response when email
      // delivery silently fails. We hand that code straight to the
      // verification page so the user isn't stranded.
      const codeParam = data && data.verification_code
        ? `&code=${encodeURIComponent(data.verification_code)}`
        : "";

      if (data && data.email_delivery_ok === false) {
        toast.success(
          isAr
            ? "تم إنشاء الحساب. لم نتمكن من إرسال البريد — استخدم الرمز الظاهر بالأسفل للتفعيل."
            : "Account created. Email delivery is temporarily down — use the code shown below to verify.",
        );
      } else {
        toast.success(
          isAr
            ? "تم إنشاء الحساب. تحقّق من بريدك للحصول على رمز التفعيل."
            : "Account created. Check your email for the verification code.",
        );
      }
      nav(`/verify?email=${encodeURIComponent(form.email)}&pending=1${nextSuffix}${codeParam}`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="register-page" className="max-w-md mx-auto px-6 py-24">
      <p className="eyebrow">{t.auth.registerTitle}</p>
      <h1 className="font-display text-4xl mt-3 tracking-tight">{t.auth.registerLead}</h1>

      <form onSubmit={onSubmit} className="mt-12 grid gap-5" data-testid="register-form">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.auth.name}</Label>
          <Input
            required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            dir="auto" data-testid="register-name" className="mt-2 rounded-sm h-11"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.auth.email}</Label>
          <Input
            type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            dir="auto" data-testid="register-email" className="mt-2 rounded-sm h-11"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.auth.phone}</Label>
          <Input
            type="tel" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder={isAr ? "+965 99 12 34 56" : "+965 99 12 34 56"}
            dir="ltr" data-testid="register-phone" className="mt-2 rounded-sm h-11"
          />
          <p className="text-xs text-slate-500 mt-1.5">
            {isAr
              ? "اختياري — لإرسال رمز التحقق عبر رسالة نصية أيضاً."
              : "Optional — also delivers the verification code via SMS."}
          </p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.auth.password}</Label>
          <Input
            type="password" required minLength={6} value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="register-password" className="mt-2 rounded-sm h-11"
          />
        </div>
        <div className="border border-slate-200 bg-slate-50 p-4 rounded-sm mt-1">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="register-agree-terms"
              className="mt-1 w-4 h-4 accent-slate-900 flex-shrink-0"
            />
            <span className="text-xs text-slate-700 leading-relaxed">
              {isAr ? (
                <>
                  أقر بأنني قرأت ووافقت على{" "}
                  <Link to="/terms" target="_blank" className="text-slate-900 underline underline-offset-2 font-medium">
                    الشروط والأحكام وسياسة الخصوصية
                  </Link>{" "}
                  الخاصة بموقع ALDAKHEELLG.COM، وأقر بأن جميع البيانات
                  والمستندات التي أقدمها صحيحة ومقدمة على مسؤوليتي الشخصية،
                  وأنني أملك الحق القانوني في مشاركتها، كما أقر بأن خدمات
                  الذكاء الاصطناعي هي خدمات مساعدة وأولية ولا تعد استشارة
                  قانونية نهائية أو بديلاً عن مراجعة محامٍ مختص.
                </>
              ) : (
                <>
                  I confirm that I have read and accepted the{" "}
                  <Link to="/terms" target="_blank" className="text-slate-900 underline underline-offset-2 font-medium">
                    Terms &amp; Privacy Policy
                  </Link>{" "}
                  of ALDAKHEELLG.COM. I confirm that the data and documents
                  I submit are accurate and shared at my own responsibility,
                  that I have the legal right to share them, and that AI
                  services on this site are assistive and preliminary — not
                  a final legal opinion or a substitute for a qualified
                  lawyer&apos;s review.
                </>
              )}
            </span>
          </label>
        </div>
        <Button
          type="submit" disabled={busy || !agreed}
          data-testid="register-submit"
          className="rounded-sm h-12 bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50"
        >
          {busy ? "…" : t.auth.submitRegister}
        </Button>
        <p className="text-sm text-slate-500 mt-2">
          {t.auth.haveAccount}{" "}
          <Link to={`/login${location.search}`} className="text-slate-900 underline underline-offset-4">
            {t.auth.login}
          </Link>
        </p>
      </form>
    </div>
  );
}
